// @flow

import React, { type Element } from 'react';
import { connect } from 'react-redux';
import AddPinRep from 'app/common/react/components/save/AddPin/AddPinRep';
import i18n from 'app/common/lib/i18n';
import MasonryPerfTiming from 'app/common/react/components/Masonry/MasonryPerfTiming';
import PinRepWithImpressions from 'app/common/react/components/DeprecatedPinRep/PinRepWithImpressions';
import ProfileEmptyState from 'app/common/react/components/profile/ProfileEmptyState/ProfileEmptyState';
import ProfileEmptyStateText from 'app/common/react/components/profile/ProfileEmptyState/ProfileEmptyStateText';
import TimeSpent from 'app/common/lib/react/TimeSpent';
import withResource from 'app/common/lib/react/withResource';
import withViewer from 'app/common/lib/react/withViewer';
import { compose } from 'redux';
import { GRID_ITEM_WIDTH } from 'app/common/lib/react/gridUtils';
import { instance as pubSub } from 'app/common/lib/PubSub';
import { type BoardPinDensity } from 'app/common/redux/actions/views';
import { type MasonryType } from 'app/common/react/ui/experimental/ExperimentalMasonry';
import { type ProfileUser } from 'app/packages/pinterest-flow-types/user';
import { type State as ReduxState } from 'app/common/redux/reducers/types';
import { type Viewer } from 'app/packages/pinterest-flow-types/viewer';
import { Box, Spinner } from 'gestalt';

const PIN_DATA_TYPE = 'pin';
const MODULE_DATA_TYPE = 'module';
const GUTTER_WIDTH = 20;

type Props = {|
  emptyState?: Element<any>,
  isGridCentered?: boolean,
  isOverview: boolean,
  isOwnProfile: boolean,
  limit?: number,
  nativePins?: boolean,
  onload?: (number) => void,
  pins: {
    data: Array<Object>,
    fetchMore: () => void,
    isFetching: boolean,
    isLoaded: boolean,
    refreshData: () => void,
  },
  profilePinView?: '' | BoardPinDensity,
  scrollContainer?: () => HTMLElement,
  showNativePins?: boolean,
  user: ProfileUser,
  username: string,
  viewer: Viewer,
|};

type State = {|
  derivedPinData: Array<{ id: string, type: string }>,
  originalPinData: Array<{}>,
  persistentPromoteButtonPins: Array<{ id: string }>,
|};

export class UserProfilePinGrid extends React.Component<Props, State> {
  static defaultProps = {
    isGridCentered: true,
  };

  componentDidMount() {
    const { pins, user } = this.props;
    this.subscribers = [
      pubSub.subscribe(user.id, PCONST.PUBSUB_TOPIC_PIN_DELETED, pins.refreshData),
    ];
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.profilePinView !== prevProps.profilePinView && this.masonryRef.current) {
      this.masonryRef.current.reflow();
    }
  }

  componentWillUnmount() {
    this.subscribers.forEach((subscriber) => subscriber.unsubscribe());
    delete this.subscribers;
  }

  masonryRef: { current: ?MasonryType } = { current: null };
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  subscribers: Array<*>;

  renderPin = ({ data: item, itemIdx }: Object) => {
    const { isOwnProfile, profilePinView, viewer } = this.props;

    switch (item.type) {
      case MODULE_DATA_TYPE:
        if (item.name === 'AddPinRep') {
          return <AddPinRep />;
        } else {
          return null;
        }
      case PIN_DATA_TYPE:
      default:
        const viewerIsPartner = isOwnProfile && viewer.isAuth && viewer.isPartner;

        const viewParam = isOwnProfile
          ? PCONST.thrift.ViewParameterType.USER_SELF
          : PCONST.thrift.ViewParameterType.USER_OTHERS;

        return (
          <PinRepWithImpressions
            denseViewSelected={profilePinView === 'dense'}
            hideAttribution
            pin={item}
            pinId={item.id}
            showCondensedInfo={isOwnProfile && profilePinView === 'dense'}
            showPinStats={viewerIsPartner}
            slotIndex={itemIdx}
            surface="UserProfilePinGrid"
            viewParameter={viewParam}
            viewType={PCONST.thrift.ViewType.USER}
          />
        );
    }
  };

  render() {
    const {
      emptyState,
      showNativePins,
      isOverview,
      isOwnProfile,
      limit,
      nativePins,
      onload,
      pins,
      profilePinView,
      user,
      username,
      isGridCentered,
      scrollContainer,
    } = this.props;
    const { fetchMore, isFetching, isLoaded } = pins;

    let pinData = pins.data || [];

    // Filter out anything that's not a pin in the dense view (i.e. the "Create Pin" rep)
    pinData = profilePinView === 'dense' ? pinData.filter((item) => item.type === 'pin') : pinData;

    // Don't show the "Create" section of the pins page at all if this is it doesn't contain pins
    // The "Saved" section will say "{{ firstName}} hasn't saved any pins yet"
    if (isLoaded && onload) {
      onload(pinData ? pinData.length : 0);
    }
    if (isLoaded && nativePins && (!isOwnProfile && (!pinData || !pinData.length))) {
      return null;
    }

    let className;
    if (limit || isOverview) {
      className = 'gridCentered mt2';
    } else if (isGridCentered && ((showNativePins && nativePins) || !showNativePins)) {
      className = 'gridCentered mt4';
    } else if (isGridCentered) {
      className = 'gridCentered';
    }
    let cacheKey = nativePins ? `${username}:pins` : `${username}:pins:native`;
    const viewParam = isOwnProfile
      ? PCONST.thrift.ViewParameterType.USER_SELF
      : PCONST.thrift.ViewParameterType.USER_OTHERS;

    const fetchMoreWithLimit = limit ? () => {} : fetchMore;

    cacheKey = !!profilePinView ? `${cacheKey}:${profilePinView}` : undefined;
    const masonry = isOverview ? (
      <MasonryPerfTiming
        cacheKey={cacheKey}
        columnWidth={profilePinView === 'dense' ? (2 / 3) * GRID_ITEM_WIDTH : GRID_ITEM_WIDTH}
        comp={this.renderPin}
        gutterWidth={profilePinView === 'dense' ? (2 / 3) * GUTTER_WIDTH : GUTTER_WIDTH}
        isFetching={isFetching}
        items={pinData}
        loadItems={fetchMoreWithLimit}
        masonryRef={this.masonryRef}
        paddingStyle="dense"
        serverRender={false}
        viewParameterType={viewParam}
        isGridCentered={isGridCentered}
        scrollContainer={scrollContainer}
      />
    ) : (
      <MasonryPerfTiming
        cacheKey={cacheKey}
        columnWidth={profilePinView === 'dense' ? (2 / 3) * GRID_ITEM_WIDTH : GRID_ITEM_WIDTH}
        comp={this.renderPin}
        gutterWidth={profilePinView === 'dense' ? (2 / 3) * GUTTER_WIDTH : GUTTER_WIDTH}
        isFetching={isFetching}
        items={pinData}
        loadItems={fetchMore}
        masonryRef={this.masonryRef}
        viewParameterType={viewParam}
        virtualize
        isGridCentered={isGridCentered}
        scrollContainer={scrollContainer}
      />
    );
    const EmptyState = emptyState || (
      <ProfileEmptyState>
        <ProfileEmptyStateText subject="pins" surface="profile" user={user} />
      </ProfileEmptyState>
    );

    return (
      <TimeSpent
        view={PCONST.thrift.ViewType.USER}
        viewParameter={viewParam}
        component={PCONST.thrift.ComponentType.USER_PINS}
      >
        <div className={className}>
          {isLoaded && (!isOwnProfile && (!pinData || !pinData.length)) ? (
            EmptyState
          ) : (
            <Box>{masonry}</Box>
          )}
          {isFetching && (
            <Box position="relative" deprecatedPadding={{ y: 3 }}>
              <Spinner
                accessibilityLabel={i18n._('Loading Pins on profile')}
                show={(showNativePins && nativePins) || !showNativePins}
              />
            </Box>
          )}
        </div>
      </TimeSpent>
    );
  }
}

const mapStateToProps = ({ ui }: ReduxState) => ({
  profilePinView: ui.views && ui.views.profilePinView,
});

export const OverviewUserPinsGrid = compose(
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  connect<Props, *, _, _, _, _>(mapStateToProps),
  withViewer,
  withResource({
    name: 'OverviewUserPinsResource',
    key: 'pins',
    options: (props) => ({
      limit: props.limit,
      exclude_add_pin_rep: props.addPinRep ? !props.addPinRep : true,
      is_own_profile_pins: props.isOwnProfile,
      user_id: props.id,
      username: props.username,
      field_set_key:
        props.viewer.isPartner && props.isOwnProfile ? 'partner_grid_item' : 'grid_item',
    }),
  }),
)(UserProfilePinGrid);

export const UserActivityPinsGrid = compose(
  withViewer,
  withResource({
    name: 'UserActivityPinsResource',
    key: 'pins',
    options: (props) => ({
      limit: props.limit,
      exclude_add_pin_rep: props.addPinRep ? !props.addPinRep : true,
      is_own_profile_pins: props.isOwnProfile,
      user_id: props.id,
      username: props.username,
      field_set_key:
        props.viewer.isPartner && props.isOwnProfile ? 'partner_grid_item' : 'grid_item',
    }),
  }),
)(UserProfilePinGrid);

export const OverviewActivitiesPinsGrid = compose(
  withViewer,
  withResource({
    name: 'OverviewUserDiscoveredPinsResource',
    key: 'pins',
    options: (props) => ({
      exclude_add_pin_rep: true,
      is_own_profile_pins: props.isOwnProfile,
      user_id: props.id,
      username: props.username,
      field_set_key:
        props.viewer.isPartner && props.isOwnProfile ? 'partner_grid_item' : 'grid_item',
    }),
  }),
)(UserProfilePinGrid);

export const ActivitiesPinsGrid = compose(
  withViewer,
  withResource({
    name: 'UserDiscoveredPinsResource',
    key: 'pins',
    options: (props) => ({
      exclude_add_pin_rep: true,
      username: props.username,
      field_set_key:
        props.viewer.isPartner && props.isOwnProfile ? 'partner_grid_item' : 'grid_item',
    }),
  }),
)(UserProfilePinGrid);

export const StoryPinsGrid = compose(
  withViewer,
  withResource({
    name: 'UserStoryPinsFeedResource',
    key: 'pins',
    options: (props) => ({
      exclude_add_pin_rep: true,
      username: props.username,
      field_set_key:
        props.viewer.isPartner && props.isOwnProfile ? 'partner_grid_item' : 'grid_item',
    }),
  }),
)(UserProfilePinGrid);

export const VideoPinsGrid = compose(
  withViewer,
  withResource({
    name: 'UserVideoPinsFeedResource',
    key: 'pins',
    options: (props) => ({
      exclude_add_pin_rep: true,
      username: props.user.username,
      field_set_key:
        props.viewer.isPartner && props.isOwnProfile ? 'partner_grid_item' : 'grid_item',
    }),
  }),
)(UserProfilePinGrid);

export default compose(
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  connect(mapStateToProps),
  withViewer,
  withResource({
    name: 'UserPinsResource',
    key: 'pins',
    options: (props) => ({
      is_own_profile_pins: props.isOwnProfile,
      username: props.username,
      field_set_key:
        props.viewer.isPartner && props.isOwnProfile ? 'partner_grid_item' : 'grid_item',
      pin_filter: null,
    }),
  }),
)(UserProfilePinGrid);
